import type {
  ClientMembershipEntry,
  PoolMatchFreelancer,
  SavedFreelancerMembership,
} from "../api/types";
import { mergeMembershipPage } from "./membership";
import { derivePoolCardOwnership } from "./pool-card-menu";

/**
 * Builds a membership page from a match payload (OQ1). Each `PoolMatchFreelancer`
 * is a superset of a `SavedFreelancerMembership`, so the pool tab hydrates its
 * saved-state directly from the match response — no redundant `/membership`
 * lookup. `saved_by_me` is derived from the row's saver id vs the signed-in
 * profile id (`derivePoolCardOwnership`: null own-id ⇒ never falsely "mine").
 *
 * Pure and DOM-free (node-testable).
 */
export const matchItemsToMembershipPage = (
  items: PoolMatchFreelancer[],
  ownProfileId: string | null | undefined,
): Record<string, SavedFreelancerMembership> => {
  const page: Record<string, SavedFreelancerMembership> = {};
  for (const it of items) {
    page[it.id] = {
      saved_freelancer_id: it.saved_freelancer_id,
      visibility: it.visibility,
      saved_by_me: derivePoolCardOwnership(
        it.saved_by_profile_id,
        ownProfileId,
      ),
      notes: it.notes,
    };
  }
  return page;
};

/**
 * Pure core of the provider's `seedFromMatch` action. Given the current entries,
 * the running `hydratedIds` dedup set, a match page, and the own profile id,
 * returns the next entries map plus the ids that were actually seeded.
 *
 * **Deletion tombstone (finding #1 — AC12).** Only ids NOT already in
 * `hydratedIds` are seeded (same dedup semantics as `hydrate`). An id that was
 * seeded/hydrated once, then optimistically removed by an un-heart, is still in
 * `hydratedIds` — so a re-seed on a cached page skips it and the deletion
 * survives the panel remount. `mergeMembershipPage` preserves only mid-flight
 * entries, never deletions, so it cannot resurrect the row either. The card keeps
 * its `removed` presentation until the background refetch drops it from the list.
 *
 * When nothing is fresh it returns the SAME `current` reference (cheap no-op for
 * the caller's `setStore`). Pure and DOM-free (node-testable).
 */
export const seedMembershipFromMatch = (
  current: Record<string, ClientMembershipEntry>,
  hydratedIds: ReadonlySet<string>,
  items: PoolMatchFreelancer[],
  ownProfileId: string | null | undefined,
): { entries: Record<string, ClientMembershipEntry>; seededIds: string[] } => {
  const fresh = items.filter((it) => it.id && !hydratedIds.has(it.id));
  if (fresh.length === 0) return { entries: current, seededIds: [] };

  const page = matchItemsToMembershipPage(fresh, ownProfileId);
  return {
    entries: mergeMembershipPage(current, page),
    seededIds: fresh.map((it) => it.id),
  };
};
